import {
  type FC,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useContext,
  useId,
  useRef,
} from "react";

import {
  Box,
  Popover,
  PopoverTrigger,
  Column,
  Portal,
  Row,
  CheckIcon,
  ChevronRightIcon,
  useDisclosure,
} from "@hightouch/htui";

import { RouterLink } from "src/router";
import { withPermission } from "src/components/permission";
import { useNavWidth } from "src/contexts/nav-width-context";
import { useSpaceContext } from "src/components/spaces";

import { CompactNavContext } from "./compact-nav-context";
import { NavItem, PermissionedNavItem } from "./nav-item";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
  PopoverHeader,
} from "./nav-popovers";
import { type ActiveSectionProps, useActiveLink } from "./utils";

export type NavSectionLink = {
  isSub?: boolean;
  href: string;
  label: string;
  rightElement?: ReactNode;
  permission?: any;
  icon?: React.ElementType;
  /** Overrides the path match, for a link the one-or-two-segment match can't place, like a query or a deeper page. */
  isActive?: boolean;
  /**
   * Additional pathnames (same `/:section/:page` shape as `href`) that should
   * also mark this link as active. Useful when a single nav entry represents
   * multiple peer routes.
   */
  activePaths?: string[];
};

type BaseNavSectionProps = {
  title: string;
  validLinks: NavSectionLink[];
  rightElement?: ReactNode;
  markerLabel?: string;
  icon: React.ElementType;
  onChange?: (name: string | null) => void;
};

type NavSectionStateProps = {
  isOpen: boolean;
  isActiveSection: boolean;
};

export type NavSectionProps = ActiveSectionProps & {
  isEnabled?: boolean;
  isSub?: boolean;
  isInFooter?: boolean;
  title: string;
  rightElement?: ReactNode;
  /** Dots the section icon while its links are hidden: a collapsed nav or a closed section. */
  markerLabel?: string;
  links: Array<NavSectionLink | false>;
  icon: React.ElementType;
  onChange?: (name: string | null) => void;
};

type ExpandedNavSectionProps = BaseNavSectionProps &
  NavSectionStateProps & {
    isSub?: boolean;
    defaultLink?: NavSectionLink;
    /** Gives the row a chevron button that opens and closes the section, while the row itself stays a link. */
    onToggle?: () => void;
  };

type CollapsedNavSectionProps = BaseNavSectionProps &
  NavSectionStateProps & {
    isOpen?: boolean;
  };

type MenuLinkProps = NavSectionLink & {
  isDisabled?: boolean;
  onClick: () => void;
};

export const NavSection: FC<Readonly<NavSectionProps>> = ({
  isEnabled,
  isSub,
  isInFooter,
  activeSection,
  openSection,
  icon,
  title,
  rightElement,
  markerLabel,
  links,
  onChange,
}) => {
  const { isNavOpen } = useNavWidth();
  const { isInSpace, isPathOrChildrenAccessibleInSpace } = useSpaceContext();

  // Filter out falsy links, then filter by space access if in a space
  const validLinks = links
    .filter((x): x is NavSectionLink => Boolean(x))
    .filter(
      (link) => !isInSpace || isPathOrChildrenAccessibleInSpace(link.href),
    );
  const defaultLink = validLinks[0];

  // Footer sections still toggle open / closed, even if "all" are open in the primary nav.
  const isOpen = isInFooter
    ? activeSection === title
    : openSection === "all" || openSection === title;
  const isActiveSection = activeSection === title;

  // Hide section if disabled or if no links remain after space filtering
  if (!isEnabled || validLinks.length === 0) {
    return null;
  }

  const sharedProps = {
    isOpen,
    isActiveSection,
    validLinks,
    title,
    rightElement,
    markerLabel,
    icon,
    onChange,
  };

  return isNavOpen ? (
    <ExpandedNavSection
      isSub={isSub}
      defaultLink={defaultLink}
      {...sharedProps}
    />
  ) : (
    <CollapsedNavSection {...sharedProps} />
  );
};

const indicatorLeft = "17px"; // var(--chakra-space-4) + 1px offset

// Product nav only, so the classic nav's disclosure keeps its current motion.
const REDUCED_MOTION_NONE = {
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
};

export const ExpandedNavSection: FC<ExpandedNavSectionProps> = ({
  isOpen,
  isActiveSection,
  isSub,
  validLinks,
  defaultLink,
  title,
  rightElement,
  markerLabel,
  icon,
  onChange,
  onToggle,
}) => {
  const isCompactNav = useContext(CompactNavContext);
  const linksId = useId();
  const handleSectionClick = () => {
    if (!isOpen) {
      onChange?.(title);
    }
  };

  return (
    <Column gap={isOpen ? 2 : 0}>
      <NavItem
        href={defaultLink?.href}
        isActiveSection={isActiveSection}
        icon={icon}
        label={title}
        rightElement={
          onToggle ? (
            <Row align="center" gap={2}>
              {rightElement}
              {/* Holds the chevron button's place, which sits over the row outside its link. */}
              <Box boxSize={6} />
            </Row>
          ) : (
            rightElement
          )
        }
        trailingAction={
          onToggle && (
            <SectionToggle
              title={title}
              isOpen={isOpen}
              controls={linksId}
              onToggle={onToggle}
            />
          )
        }
        // An open section shows its links' own markers.
        markerLabel={isOpen ? undefined : markerLabel}
        onClick={handleSectionClick}
      />
      {validLinks.length > 0 && (
        <Column
          id={linksId}
          display="grid"
          gridTemplateRows={isOpen ? "1fr" : "0fr"}
          overflowY="hidden"
          willChange={isOpen ? "grid-template-rows" : "none"}
          transitionProperty="grid-template-rows"
          transitionDuration="slow"
          transitionTimingFunction="easeInOutQuad"
          visibility={isOpen ? "visible" : "hidden"}
          _focusWithin={{
            overflowY: "visible !important",
          }}
          sx={isCompactNav ? REDUCED_MOTION_NONE : undefined}
        >
          <NestedNavLinks links={validLinks} isSub={isSub} />
        </Column>
      )}
    </Column>
  );
};

type SectionToggleProps = {
  title: string;
  isOpen: boolean;
  /** The id of the section's links. */
  controls: string;
  onToggle: () => void;
};

/** Opens and closes a section's links without going to its page. */
const SectionToggle = ({
  title,
  isOpen,
  controls,
  onToggle,
}: SectionToggleProps) => (
  <Box
    as="button"
    type="button"
    aria-label={`${title} pages`}
    aria-expanded={isOpen}
    aria-controls={controls}
    display="flex"
    alignItems="center"
    justifyContent="center"
    boxSize={6}
    borderRadius="md"
    color="text.tertiary"
    opacity={isOpen ? 1 : 0}
    // Hidden, it leaves a tap to the row's link.
    pointerEvents={isOpen ? "auto" : "none"}
    transitionProperty="opacity, color"
    transitionDuration="fast"
    transitionTimingFunction="easeOutQuad"
    _hover={{ color: "text.primary" }}
    _focusVisible={{ outline: "none", boxShadow: "outline", opacity: 1 }}
    sx={{
      "[data-nav-hovered] &": { opacity: 1, pointerEvents: "auto" },
      // A touch screen has no hover to reveal the button, so it always shows.
      "@media (hover: none)": { opacity: 1, pointerEvents: "auto" },
    }}
    onClick={onToggle}
  >
    <Box
      as={ChevronRightIcon}
      aria-hidden="true"
      boxSize={4}
      transform={isOpen ? "rotate(90deg)" : "none"}
      transitionProperty="transform"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      sx={{
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    />
  </Box>
);

type NestedNavLinksProps = {
  links: Array<NavSectionLink & { isActive?: boolean }>;
  isSub?: boolean;
};

/** Links nested under a nav item, on a rail that marks the hovered and active one. */
export const NestedNavLinks = ({ links, isSub }: NestedNavLinksProps) => {
  return (
    <Column pos="relative" minHeight={0}>
      {/* Indicator rail */}
      <Box
        aria-hidden="true"
        bg="gray.300"
        borderRadius="full"
        pos="absolute"
        left={indicatorLeft}
        top={2}
        bottom={2}
        width="2px"
      />
      {links.map((link) => (
        <PermissionedNavItem key={link.href} isNested isSub={isSub} {...link} />
      ))}
    </Column>
  );
};

const CollapsedNavSection: FC<CollapsedNavSectionProps> = ({
  isOpen,
  isActiveSection,
  validLinks,
  title,
  markerLabel,
  icon,
  onChange,
}) => {
  const linksRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popover = useDisclosure();
  // Set while Escape returns focus to the trigger, whose focus would otherwise reopen the hover popover.
  const isReturningFocus = useRef(false);
  // A hover popover stays open while the pointer rests on it, so a chosen link closes it, over the loading page too.
  const handleMenuClick = () => {
    onChange?.(title);
    popover.onClose();
  };
  const handleOpen = () => {
    if (isReturningFocus.current) return;
    popover.onOpen();
  };
  // A hover popover never returns focus, so Escape inside it would drop focus to the body.
  const handleLinksKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    isReturningFocus.current = true;
    triggerRef.current?.focus();
    popover.onClose();
    setTimeout(() => {
      isReturningFocus.current = false;
    });
  };

  return (
    // Box wrapper required so nav gap does not apply to the popover
    <Box>
      <Popover
        trigger="hover"
        placement="right-start"
        offset={[0, 16]}
        isOpen={popover.isOpen}
        onOpen={handleOpen}
        onClose={popover.onClose}
      >
        {({ isOpen: isPopoverOpen }) => (
          <>
            <PopoverTrigger>
              <NavItem
                ref={triggerRef}
                isActive={isOpen && isActiveSection}
                isHovered={isPopoverOpen}
                hasCollapsedTooltip={false} // Disable tooltip since it has a popover
                icon={icon}
                label={title}
                markerLabel={markerLabel}
                onClick={(event) => focusFirstLinkOnKeyboard(event, linksRef)}
              />
            </PopoverTrigger>

            <Portal>
              <NavPopoverContentAndBody ariaLabel={title}>
                <PopoverHeader title={title} />
                <Column ref={linksRef} onKeyDown={handleLinksKeyDown}>
                  {validLinks.map((link) => (
                    <PermissionedMenuLink
                      key={link.href}
                      {...link}
                      onClick={handleMenuClick}
                    />
                  ))}
                </Column>
              </NavPopoverContentAndBody>
            </Portal>
          </>
        )}
      </Popover>
    </Box>
  );
};

/**
 * A hover popover opens on focus but stays out of the tab order, since it is portaled.
 * Enter or Space on its trigger moves focus into it, so keyboard users can reach its links.
 */
const focusFirstLinkOnKeyboard = (
  event: MouseEvent,
  containerRef: RefObject<HTMLElement>,
) => {
  // A click from Enter or Space reports no pointer presses.
  if (event.detail !== 0) return;
  requestAnimationFrame(() => {
    containerRef.current?.querySelector<HTMLElement>("a, button")?.focus();
  });
};

const MenuLink = forwardRef<HTMLDivElement, MenuLinkProps>((props, ref) => {
  const isPathActive = useActiveLink(props);
  const isActive = props.isActive ?? isPathActive;

  return (
    <Box
      ref={ref}
      as={props.isDisabled ? "button" : RouterLink}
      // @ts-expect-error - `as` prop
      to={props.href}
      preload="intent"
      display="flex"
      alignItems="center"
      bg={isActive ? "primary.background" : "white"}
      color={isActive ? "primary.base" : "text.primary"}
      h={10}
      justifyContent="space-between"
      opacity={props.isDisabled ? 0.5 : 1}
      pointerEvents={props.isDisabled ? "none" : "auto"}
      _hover={isActive ? undefined : { bg: "base.background" }}
      onClick={props.onClick}
      {...NAV_POPOVER_MENU_ITEM_STYLES}
    >
      {props.label}
      {(props.rightElement || isActive) && (
        <Row align="center" gap={2}>
          {props.rightElement}
          {isActive && (
            <Box as={CheckIcon} color="primary.base" fontSize="lg" />
          )}
        </Row>
      )}
    </Box>
  );
});

MenuLink.displayName = "MenuLink";

const PermissionedMenuLink = withPermission(MenuLink);
